<template>
  <!-- Modal de Upgrade/Demo -->
  <div v-if="isVisible" class="fixed inset-0 bg-black/60 backdrop-blur-sm overflow-y-auto h-full w-full z-[150] flex items-start sm:items-center justify-center p-3 sm:p-4" @click="handleBackdropClick">
    <div class="relative w-full max-w-2xl shadow-2xl rounded-2xl bg-white my-4 sm:my-0 max-h-[90vh] overflow-y-auto" @click.stop>
      
      <!-- Header simple y sobrio -->
      <div class="flex items-center justify-between px-6 py-5 border-b border-gray-100">
        <div>
          <h3 class="text-lg font-bold text-gray-900">
            {{ $t('upgradeToProModal.title') }}
          </h3>
          <p class="text-xs text-gray-500 mt-1">
            {{ $t('upgradeToProModal.subtitle') }}
          </p>
        </div>
        <button @click="closeModal" class="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Contenido del Modal -->
      <div class="px-6 py-6">
        
        <!-- Grid de características principales -->
        <div class="grid sm:grid-cols-2 gap-3 mb-6">
          
          <!-- Característica 1: Contratos digitales -->
          <div class="flex gap-3 p-4 rounded-xl border border-gray-200 hover:border-gray-300 transition-colors bg-white">
            <div class="flex-shrink-0">
              <div class="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                <svg class="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
            </div>
            <div class="flex-1 min-w-0">
              <h4 class="font-semibold text-gray-900 text-sm mb-1">{{ $t('upgradeToProModal.features.digitalContracts.title') }}</h4>
              <p class="text-xs text-gray-500 leading-relaxed">{{ $t('upgradeToProModal.features.digitalContracts.desc') }}</p>
            </div>
          </div>

          <!-- Característica 2: Pagos automáticos -->
          <div class="flex gap-3 p-4 rounded-xl border border-gray-200 hover:border-gray-300 transition-colors bg-white">
            <div class="flex-shrink-0">
              <div class="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                <svg class="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
              </div>
            </div>
            <div class="flex-1 min-w-0">
              <h4 class="font-semibold text-gray-900 text-sm mb-1">{{ $t('upgradeToProModal.features.payments.title') }}</h4>
              <p class="text-xs text-gray-500 leading-relaxed">{{ $t('upgradeToProModal.features.payments.desc') }}</p>
            </div>
          </div>

          <!-- Característica 3: IA de selección -->
          <div class="flex gap-3 p-4 rounded-xl border border-gray-200 hover:border-gray-300 transition-colors bg-white">
            <div class="flex-shrink-0">
              <div class="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                <svg class="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
            </div>
            <div class="flex-1 min-w-0">
              <h4 class="font-semibold text-gray-900 text-sm mb-1">{{ $t('upgradeToProModal.features.aiScreening.title') }}</h4>
              <p class="text-xs text-gray-500 leading-relaxed">{{ $t('upgradeToProModal.features.aiScreening.desc') }}</p>
            </div>
          </div>

          <!-- Característica 4: Marketplace -->
          <div class="flex gap-3 p-4 rounded-xl border border-gray-200 hover:border-gray-300 transition-colors bg-white">
            <div class="flex-shrink-0">
              <div class="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                <svg class="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>
            <div class="flex-1 min-w-0">
              <h4 class="font-semibold text-gray-900 text-sm mb-1">{{ $t('upgradeToProModal.features.marketplace.title') }}</h4>
              <p class="text-xs text-gray-500 leading-relaxed">{{ $t('upgradeToProModal.features.marketplace.desc') }}</p>
            </div>
          </div>

          <!-- Característica 5: Dashboard en tiempo real -->
          <div class="flex gap-3 p-4 rounded-xl border border-gray-200 hover:border-gray-300 transition-colors bg-white">
            <div class="flex-shrink-0">
              <div class="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                <svg class="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
            </div>
            <div class="flex-1 min-w-0">
              <h4 class="font-semibold text-gray-900 text-sm mb-1">{{ $t('upgradeToProModal.features.dashboard.title') }}</h4>
              <p class="text-xs text-gray-500 leading-relaxed">{{ $t('upgradeToProModal.features.dashboard.desc') }}</p>
            </div>
          </div>

          <!-- Característica 6: Gestión de equipos -->
          <div class="flex gap-3 p-4 rounded-xl border border-gray-200 hover:border-gray-300 transition-colors bg-white">
            <div class="flex-shrink-0">
              <div class="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                <svg class="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
            </div>
            <div class="flex-1 min-w-0">
              <h4 class="font-semibold text-gray-900 text-sm mb-1">{{ $t('upgradeToProModal.features.teams.title') }}</h4>
              <p class="text-xs text-gray-500 leading-relaxed">{{ $t('upgradeToProModal.features.teams.desc') }}</p>
            </div>
          </div>

        </div>

        <!-- Separador -->
        <div class="h-px bg-gray-100 mb-6"></div>

        <!-- Beneficios adicionales (lista compacta) -->
        <div class="bg-gray-50 rounded-xl p-4 mb-6 border border-gray-100">
          <h4 class="font-semibold text-gray-900 text-sm mb-3 text-center">{{ $t('upgradeToProModal.additionalBenefits') }}</h4>
          <div class="grid sm:grid-cols-2 gap-2">
            <div class="flex items-center gap-2">
              <svg class="w-4 h-4 text-gray-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
              </svg>
              <span class="text-xs text-gray-700">{{ $t('upgradeToProModal.benefits.unlimitedColivings') }}</span>
            </div>
            <div class="flex items-center gap-2">
              <svg class="w-4 h-4 text-gray-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
              </svg>
              <span class="text-xs text-gray-700">{{ $t('upgradeToProModal.benefits.calendar') }}</span>
            </div>
            <div class="flex items-center gap-2">
              <svg class="w-4 h-4 text-gray-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
              </svg>
              <span class="text-xs text-gray-700">{{ $t('upgradeToProModal.benefits.reports') }}</span>
            </div>
            <div class="flex items-center gap-2">
              <svg class="w-4 h-4 text-gray-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
              </svg>
              <span class="text-xs text-gray-700">{{ $t('upgradeToProModal.benefits.branding') }}</span>
            </div>
          </div>
        </div>

        <!-- Formulario de Contacto -->
        <div class="rounded-xl p-5 border border-gray-200 bg-white">
          <h4 class="font-semibold text-gray-900 mb-4 text-center">
            {{ $t('upgradeToProModal.form.title') }}
          </h4>
          <form @submit.prevent="submitUpgradeRequest" class="space-y-3">
            <div>
              <label for="contactEmail" class="block text-xs font-medium text-gray-600 mb-1.5">
                {{ $t('upgradeToProModal.form.emailLabel') }}
              </label>
              <input
                id="contactEmail"
                v-model="upgradeForm.email"
                type="email"
                required
                class="w-full px-3.5 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-gray-900 bg-white outline-none transition"
                :placeholder="$t('upgradeToProModal.form.emailPlaceholder')"
              />
            </div>
            
            <div>
              <label for="contactPhone" class="block text-xs font-medium text-gray-600 mb-1.5">
                {{ $t('upgradeToProModal.form.phoneLabel') }}
              </label>
              <input
                id="contactPhone"
                v-model="upgradeForm.phone"
                type="tel"
                class="w-full px-3.5 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-gray-900 bg-white outline-none transition"
                :placeholder="$t('upgradeToProModal.form.phonePlaceholder')"
              />
            </div>
            
            <div>
              <label for="contactMessage" class="block text-xs font-medium text-gray-600 mb-1.5">
                {{ $t('upgradeToProModal.form.messageLabel') }}
              </label>
              <textarea
                id="contactMessage"
                v-model="upgradeForm.message"
                rows="2"
                class="w-full px-3.5 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-gray-900 resize-none bg-white outline-none transition"
                :placeholder="$t('upgradeToProModal.form.messagePlaceholder')"
              ></textarea>
            </div>
            
            <div class="flex gap-3 pt-2">
              <button
                type="button"
                @click="closeModal"
                class="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
              >
                {{ $t('upgradeToProModal.form.cancelButton') }}
              </button>
              <button
                type="submit"
                :disabled="isSubmittingUpgrade"
                class="flex-1 px-4 py-2.5 rounded-lg text-sm font-semibold text-white bg-gray-900 hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <span v-if="isSubmittingUpgrade">{{ $t('upgradeToProModal.form.submitting') }}</span>
                <span v-else>{{ $t('upgradeToProModal.form.submitButton') }}</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>

  <!-- Toast de Éxito -->
  <div v-if="showSuccessMessage" class="fixed bottom-4 right-4 max-w-sm w-full bg-white shadow-lg rounded-lg pointer-events-auto ring-1 ring-black ring-opacity-5 z-50">
    <div class="p-4">
      <div class="flex items-start">
        <div class="flex-shrink-0">
          <svg class="h-6 w-6 text-gray-900" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <div class="ml-3 w-0 flex-1 pt-0.5">
          <p class="text-sm font-medium text-gray-900">{{ $t('upgradeToProModal.success.title') }}</p>
          <p class="mt-1 text-sm text-gray-500">{{ $t('upgradeToProModal.success.message') }}</p>
        </div>
        <div class="ml-4 flex-shrink-0 flex">
          <button @click="showSuccessMessage = false" class="bg-white rounded-md inline-flex text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900">
            <span class="sr-only">Cerrar</span>
            <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { getAuth } from 'firebase/auth'
import { getDoc, doc } from 'firebase/firestore'
import { db } from '../../firebase'

const { t } = useI18n()

// Props
const props = defineProps({
  isVisible: {
    type: Boolean,
    default: false
  }
})

// Emits
const emit = defineEmits(['close', 'success'])

// Variables reactivas del formulario
const upgradeForm = ref({
  email: '',
  phone: '',
  message: ''
})

// Estados
const isSubmittingUpgrade = ref(false)
const showSuccessMessage = ref(false)

// Referencias
const auth = getAuth()

// Watcher para pre-rellenar el email cuando se abre el modal
watch(() => props.isVisible, (newValue) => {
  if (newValue) {
    // Pre-rellenar el email del usuario actual
    const currentUser = auth.currentUser
    if (currentUser && currentUser.email) {
      upgradeForm.value.email = currentUser.email
    }
  }
})

// Funciones
const closeModal = () => {
  // Limpiar el formulario
  upgradeForm.value = {
    email: '',
    phone: '',
    message: ''
  }
  emit('close')
}

const handleBackdropClick = () => {
  closeModal()
}

const submitUpgradeRequest = async () => {
  if (isSubmittingUpgrade.value) return
  
  try {
    isSubmittingUpgrade.value = true
    
    // Preparar datos del agente (puede ser usuario autenticado o anónimo)
    let agentData = null
    let authHeaders = { 'Content-Type': 'application/json' }
    
    const currentUser = auth.currentUser
    
    if (currentUser) {
      // Usuario autenticado: obtener datos completos de Firestore
      try {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid))
        const userData = userDoc.data()
        
        if (userData) {
          agentData = {
            agentId: currentUser.uid,
            email: currentUser.email,
            firstName: userData.firstName || '',
            lastName: userData.lastName || '',
            fullName: userData.displayName || userData.fullName || `${userData.firstName || ''} ${userData.lastName || ''}`.trim(),
            company: userData.company || '',
            phone: userData.phone || '',
            stats: {
              totalColivings: userData.totalColivings || 0,
              totalContracts: userData.totalContracts || 0
            }
          }
        }
        
        // Añadir token de autorización si hay usuario
        const token = await currentUser.getIdToken()
        authHeaders = {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      } catch (userError) {
        console.warn('No se pudieron obtener datos del usuario, continuando como anónimo:', userError)
      }
    }
    
    // Si no hay datos del agente (usuario no autenticado), crear datos básicos del formulario
    if (!agentData) {
      agentData = {
        agentId: 'anonymous_' + Date.now(),
        email: upgradeForm.value.email,
        firstName: '',
        lastName: '',
        fullName: 'Usuario no registrado',
        company: '',
        phone: upgradeForm.value.phone || '',
        stats: { totalColivings: 0, totalContracts: 0 }
      }
    }

    // Enviar solicitud al backend
    const response = await fetch('/api/email/send-agent-upgrade-request', {
      method: 'POST',
      headers: authHeaders,
      body: JSON.stringify({
        agentEmail: upgradeForm.value.email,
        agentPhone: upgradeForm.value.phone,
        agentMessage: upgradeForm.value.message,
        agentData: agentData
      })
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.message || 'Error en el servidor')
    }

    const result = await response.json()
    console.log('Solicitud de upgrade enviada exitosamente:', result)
    
    // Cerrar modal
    closeModal()
    
    // Mostrar mensaje de éxito como toast
    showSuccessMessage.value = true
    
    // Emitir evento de éxito al componente padre
    emit('success')
    
    // Auto-ocultar el toast después de 5 segundos
    setTimeout(() => {
      showSuccessMessage.value = false
    }, 5000)
    
  } catch (error) {
    console.error('Error enviando solicitud de actualización:', error)
    alert('Error al enviar la solicitud. Por favor, intenta de nuevo.')
  } finally {
    isSubmittingUpgrade.value = false
  }
}
</script>
